import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  alpha,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Person,
  PostAdd,
  Group,
  Visibility,
  CalendarToday,
  ChatBubble,
  ThumbUp,
  ThumbDown,
  Coffee,
  Search,
  Clear,
  People,
  Forum,
  Category,
  ArrowForward,
  Email,
  Image as ImageIcon,
  Videocam,
  TextFields,
} from '@mui/icons-material';
import { communityApi, Community } from '../services/communityApi';
import { postApi } from '../services/postApi';
import { Post } from '../types/community';
import { userApi } from '../services/userApi';
import logo from '../assets/stats-WObg.png';
import { SBlock } from '../components/skeletons';

interface User {
  _id: string;
  username: string;
  email: string;
  profilePicture?: string;
  role: string;
  createdAt: string;
}

interface AdminStats {
  totalUsers: number;
  totalPosts: number;
  totalCommunities: number;
  activeUsers: number;
  totalComments: number;
}

const AdminStats: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalPosts: 0,
    totalCommunities: 0,
    activeUsers: 0,
    totalComments: 0,
  });
  const [loading, setLoading] = useState({
    users: true,
    posts: true,
    communities: true,
    stats: true,
  });
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState<'users' | 'posts' | 'communities'>('users');
  
  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  useEffect(() => {
    if (user) {
      fetchAllData();
    }
  }, [user]);

  const fetchAllData = async () => {
    try {
      setLoading({
        users: true,
        posts: true,
        communities: true,
        stats: true,
      });

      // Fetch all data in parallel
      await Promise.all([
        fetchUsers(),
        fetchPosts(),
        fetchCommunities(),
      ]);
    } catch (err: any) {
      console.error('Error fetching admin data:', err);
      setError(err.message || 'Failed to load admin data');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userApi.getAllUsers(100); // Increased limit to 100
      if (response.success && response.users) {
        setUsers(response.users);
        // Update stats
        setStats(prev => ({
          ...prev,
          totalUsers: response.users.length,
          activeUsers: response.users.length, // All users are active for now
        }));
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(prev => ({ ...prev, users: false, stats: false }));
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await postApi.getPosts({ 
        limit: 100, // Increased limit to 100
        sortBy: 'newest' // Get newest posts first
      });
      if (response.success) {
        setPosts(response.posts);
        // Calculate total comments
        const totalComments = response.posts.reduce((sum, post) => sum + (post.commentsCount || 0), 0);
        setStats(prev => ({
          ...prev,
          totalPosts: response.posts.length,
          totalComments: totalComments,
        }));
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(prev => ({ ...prev, posts: false }));
    }
  };

  const fetchCommunities = async () => {
    try {
      const response = await communityApi.getCommunities({ 
        limit: 100, // Increased limit to 100
        sortBy: 'members' // Sort by most members
      });
      if (response.success && response.communities) {
        setCommunities(response.communities);
        setStats(prev => ({
          ...prev,
          totalCommunities: response.communities.length,
        }));
      }
    } catch (err) {
      console.error('Error fetching communities:', err);
    } finally {
      setLoading(prev => ({ ...prev, communities: false }));
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getInitials = (username: string) => {
    if (!username) return '??';
    return username
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleClearSearch = () => setSearchTerm('');

  const handleViewUser = (username: string) => {
  if (username) {
    navigate(`/${username}`);
  }
  };

  const handleViewPost = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  const handleViewCommunity = (slug: string) => {
    navigate(`/community/${slug}`);
  };

  const StatCard = ({ title, value, icon }: any) => (
    <Paper
      elevation={0}
      sx={{
        ...glassEffect,
        p: 3,
        borderRadius: '16px',
        flex: 1,
        minWidth: { xs: '100%', sm: '45%', md: 'auto' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            bgcolor: alpha(goldColor, 0.1),
            borderRadius: '12px',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { 
            sx: { 
              color: goldColor,
              fontSize: { xs: 24, md: 32 }
            } 
          })}
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: isDark ? '#fff' : deepGreen,
              fontWeight: 800,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  const UserCard = ({ user }: { user: User }) => (
    <Card 
      sx={{ 
        height: 'auto',
        minWidth: { xs: '300px', sm: '400px' },
        maxWidth: { xs: '100%', sm: '400px' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        ...glassEffect,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isDark 
            ? '0 12px 40px rgba(196, 182, 111, 0.3)'
            : '0 12px 40px rgba(5, 24, 20, 0.2)',
        }
      }}
    >
      <CardContent sx={{ flex: 1, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={user.profilePicture}
            sx={{
              width: 60,
              height: 60,
              bgcolor: alpha(goldColor, 0.1),
              color: goldColor,
              fontWeight: 700,
              fontSize: '1.2rem',
              border: `2px solid ${goldColor}`,
            }}
          >
            {getInitials(user.username)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 800, 
              color: isDark ? '#fff' : deepGreen, 
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user.username || 'Unknown User'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Email sx={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)' }} />
              <Typography variant="body2" sx={{ 
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {user.email || 'No email'}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Chip
          label={user.role || 'user'}
          size="small"
          sx={{
            bgcolor: alpha(goldColor, isDark ? 0.15 : 0.1),
            color: goldColor,
            fontWeight: 600,
            fontSize: '0.75rem',
            mb: 2,
          }}
        />
        
        {user.createdAt && (
          <Typography variant="body2" sx={{ 
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
            <CalendarToday sx={{ fontSize: 14 }} />
            Joined: {formatDate(user.createdAt)}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleViewUser(user.username)}
          endIcon={<ArrowForward />}
          sx={{
            borderColor: goldColor,
            color: goldColor,
            borderRadius: '50px',
            fontWeight: 600,
            '&:hover': {
              borderColor: goldColor,
              bgcolor: alpha(goldColor, 0.05),
            }
          }}
        >
          View Profile
        </Button>
      </CardActions>
    </Card>
  );

  const PostCard = ({ post }: { post: Post }) => {
    const isMediaPost = post.type === 'image' || post.type === 'video';
    const mediaUrl = post.mediaUrl || (post.images && post.images[0]);
    
    return (
      <Card 
        sx={{ 
          height: 'auto',
          minWidth: { xs: '300px', sm: '400px' },
          maxWidth: { xs: '100%', sm: '400px' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          ...glassEffect,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDark 
              ? '0 12px 40px rgba(196, 182, 111, 0.3)'
              : '0 12px 40px rgba(5, 24, 20, 0.2)',
          }
        }}
      >
        {isMediaPost && mediaUrl && (
          <Box sx={{ 
            height: 200, 
            position: 'relative',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            overflow: 'hidden',
          }}>
            {post.type === 'image' ? (
              <img
                src={mediaUrl}
                alt={post.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box sx={{
                width: '100%',
                height: '100%',
                bgcolor: alpha(goldColor, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Videocam sx={{ fontSize: 48, color: goldColor, opacity: 0.5 }} />
              </Box>
            )}
            <Chip
              icon={post.type === 'image' ? <ImageIcon /> : <Videocam />}
              label={post.type === 'image' ? 'Image' : 'Video'}
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: alpha(goldColor, 0.9),
                color: isDark ? deepGreen : '#fff',
                fontWeight: 600,
              }}
            />
          </Box>
        )}
        
        <CardContent sx={{ 
          flex: 1, 
          p: 3,
          pt: isMediaPost ? 2 : 3 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {!isMediaPost && (
              <Chip
                icon={<TextFields />}
                label="Text"
                size="small"
                sx={{
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          
          <Typography variant="h6" sx={{ 
            fontWeight: 800, 
            color: isDark ? '#fff' : deepGreen, 
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            {post.title}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Avatar
                src={post.author?.profilePicture}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                }}
              >
                {getInitials(post.author?.username)}
              </Avatar>
              <Typography variant="body2" sx={{ 
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {post.author?.username || 'Unknown'}
              </Typography>
            </Box>
            
            {post.community && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Group sx={{ fontSize: 16, color: goldColor }} />
                <Typography variant="body2" sx={{ 
                  color: goldColor, 
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {post.community.name}
                </Typography>
              </Box>
            )}
          </Box>
          
          {post.content && (
            <Typography variant="body2" sx={{ 
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}>
              {post.content}
            </Typography>
          )}
          
          <Divider sx={{ my: 2, borderColor: alpha(isDark ? '#fff' : deepGreen, 0.1) }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ThumbUp sx={{ fontSize: 16, color: goldColor }} />
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)' }}>
                  {post.upvotes || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ChatBubble sx={{ fontSize: 16, color: goldColor }} />
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)' }}>
                  {post.commentsCount || 0}
                </Typography>
              </Box>
            </Box>
            {post.createdAt && (
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)' }}>
                {formatDate(post.createdAt)}
              </Typography>
            )}
          </Box>
        </CardContent>
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleViewPost(post.id)}
            endIcon={<ArrowForward />}
            sx={{
              borderColor: goldColor,
              color: goldColor,
              borderRadius: '50px',
              fontWeight: 600,
              '&:hover': {
                borderColor: goldColor,
                bgcolor: alpha(goldColor, 0.05),
              }
            }}
          >
            View Post
          </Button>
        </CardActions>
      </Card>
    );
  };

  const CommunityCard = ({ community }: { community: Community }) => (
    <Card 
      sx={{ 
        height: 'auto',
        minWidth: { xs: '300px', sm: '400px' },
        maxWidth: { xs: '100%', sm: '400px' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        ...glassEffect,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isDark 
            ? '0 12px 40px rgba(196, 182, 111, 0.3)'
            : '0 12px 40px rgba(5, 24, 20, 0.2)',
        }
      }}
    >
      <CardContent sx={{ flex: 1, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={community.profilePicture}
            sx={{
              width: 60,
              height: 60,
              bgcolor: alpha(goldColor, 0.1),
              color: goldColor,
              fontWeight: 700,
              fontSize: '1.5rem',
              border: `2px solid ${goldColor}`,
            }}
          >
            {community.name?.charAt(0).toUpperCase() || 'C'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 800, 
              color: isDark ? '#fff' : deepGreen, 
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {community.name || 'Unknown Community'}
            </Typography>
            {community.category && (
              <Chip
                label={community.category}
                size="small"
                sx={{
                  bgcolor: alpha(goldColor, isDark ? 0.15 : 0.1),
                  color: goldColor,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Box>
        </Box>
        
        {community.description && (
          <Typography variant="body2" sx={{ 
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
            mb: 2,
            fontStyle: 'italic',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            "{community.description}"
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People sx={{ fontSize: 16, color: goldColor }} />
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)' }}>
              {community.membersCount || 0} members
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PostAdd sx={{ fontSize: 16, color: goldColor }} />
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)' }}>
              {community.postsCount || 0} posts
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2, borderColor: alpha(isDark ? '#fff' : deepGreen, 0.1) }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Avatar
            src={community.owner?.profilePicture}
            sx={{
              width: 28,
              height: 28,
              bgcolor: alpha(goldColor, 0.1),
              color: goldColor,
              fontWeight: 700,
              fontSize: '0.8rem',
            }}
          >
            {getInitials(community.owner?.username)}
          </Avatar>
          <Typography variant="body2" sx={{ 
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            Owner: {community.owner?.username || 'Unknown'}
          </Typography>
        </Box>
        
        {community.createdAt && (
          <Typography variant="caption" sx={{ 
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
            <CalendarToday sx={{ fontSize: 12 }} />
            Created: {formatDate(community.createdAt)}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleViewCommunity(community.slug)}
          endIcon={<ArrowForward />}
          sx={{
            borderColor: goldColor,
            color: goldColor,
            borderRadius: '50px',
            fontWeight: 600,
            '&:hover': {
              borderColor: goldColor,
              bgcolor: alpha(goldColor, 0.05),
            }
          }}
        >
          View Community
        </Button>
      </CardActions>
    </Card>
  );

  const LoadingGrid = ({ count = 4 }: { count?: number }) => (
    <Grid container spacing={3} justifyContent="center">
      {Array.from({ length: count }).map((_, index) => (
        <Grid  key={index}>
          <Paper
            sx={{
              height: 300,
              minWidth: '400px',
              maxWidth: '400px',
              borderRadius: '16px',
              ...glassEffect,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 3,
            }}
          >
            {/* Chart placeholder: a title bar over a plot area */}
            <SBlock h={16} w="45%" />
            <SBlock h={200} r="12px" />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: isDark ? '#000000' : '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, borderRadius: '32px', textAlign: 'center', ...glassEffect }}>
            <Coffee sx={{ fontSize: 80, color: goldColor, mb: 3 }} />
            <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>Admin Access Required</Typography>
            <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>You need to be logged in as an admin to access this panel.</Typography>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>Go to Login</Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const isLoading = loading.users && loading.posts && loading.communities;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      overflowX: 'hidden',
    }}>
      <Container maxWidth="xl" sx={{ 
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        {/* Back button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)} 
          sx={{ 
            mb: 3, 
            color: goldColor, 
            '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } 
          }}
        >
          Back
        </Button>

        {/* Header with Logo */}
        <Paper sx={{ 
          p: 0, 
          mb: 6, 
          borderRadius: '32px', 
          overflow: 'hidden', 
          ...glassEffect, 
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', md: 'row' }, 
          alignItems: 'center',
          textAlign: { xs: 'center', md: 'left' },
        }}>
          <Box sx={{ 
            p: { xs: 3, md: 6 }, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
              <People sx={{ fontSize: { xs: 32, md: 40 }, color: goldColor, mr: 2 }} />
              <Typography variant="h3" sx={{ 
                ...discordFont, 
                background: isDark ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`, 
                backgroundClip: 'text', 
                WebkitBackgroundClip: 'text', 
                color: 'transparent', 
                fontSize: { xs: '1.75rem', md: '3rem' },
                textAlign: { xs: 'center', md: 'left' },
              }}>
                Platform Overview
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
              fontWeight: 400, 
              maxWidth: 600,
              textAlign: { xs: 'center', md: 'left' },
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}>
              Monitor all platform activities, users, posts, and communities from a single dashboard.
            </Typography>
          </Box>
          <Box sx={{ 
                      width: { xs: '100%', md: '40%' }, 
                      height: { xs: 180, md: '100%' }, 
                      minHeight: { md: 300 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      p: { xs: 2, md: 4 } 
                    }}>
                       <img src={logo} alt="Admin Hero" style={{ 
                         width: '100%', 
                         height: '100%', 
                         objectFit: 'contain', 
                         maxHeight: 250 
                       }} />
                    </Box>
        </Paper>

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mb: 4, flexWrap: 'wrap' }}>
          <StatCard title="Total Users" value={stats.totalUsers} icon={<People />} />
          <StatCard title="Total Posts" value={stats.totalPosts} icon={<PostAdd />} />
          <StatCard title="Total Communities" value={stats.totalCommunities} icon={<Group />} />
          <StatCard title="Total Comments" value={stats.totalComments} icon={<ChatBubble />} />
        </Box>

        {/* Search and Filter Section */}
        <Paper sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '20px', 
          ...glassEffect,
        }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={`Search ${activeSection}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.05)',
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: goldColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: goldColor,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isDark ? '#FFFFFF' : deepGreen,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: goldColor, opacity: 0.7 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear sx={{ fontSize: 18, color: goldColor }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Section Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeSection}
              onChange={(_, value) => setActiveSection(value)}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              sx={{
                '& .MuiTab-root': {
                  color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: { xs: 1, sm: 3 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '&.Mui-selected': {
                    color: goldColor,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: goldColor,
                  borderRadius: '3px',
                },
              }}
            >
              <Tab icon={<Person />} iconPosition="start" label={`Users (${users.length})`} value="users" />
              <Tab icon={<PostAdd />} iconPosition="start" label={`Posts (${posts.length})`} value="posts" />
              <Tab icon={<Group />} iconPosition="start" label={`Communities (${communities.length})`} value="communities" />
            </Tabs>
          </Box>
        </Paper>

        {/* Content Section */}
        <Paper sx={{ 
          p: 3, 
          borderRadius: '20px', 
          ...glassEffect,
          minHeight: 400,
        }}>
          {isLoading ? (
            <LoadingGrid />
          ) : (
            <>
              {activeSection === 'users' && (
                <>
                  <Typography variant="h5" sx={{ 
                    ...discordFont, 
                    color: isDark ? '#fff' : deepGreen, 
                    mb: 3,
                    fontSize: '1.5rem',
                  }}>
                    Platform Users ({users.length})
                  </Typography>
                  {users.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Person sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
                      <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>No Users Found</Typography>
                      <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>No users are currently registered on the platform.</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3} justifyContent="center">
                      {users
                        .filter(user => 
                          searchTerm === '' || 
                          (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
                        )
                        .map(user => (
                          <Grid  key={user._id}>
                            <UserCard user={user} />
                          </Grid>
                        ))}
                    </Grid>
                  )}
                </>
              )}

              {activeSection === 'posts' && (
                <>
                  <Typography variant="h5" sx={{ 
                    ...discordFont, 
                    color: isDark ? '#fff' : deepGreen, 
                    mb: 3,
                    fontSize: '1.5rem',
                  }}>
                    Recent Posts ({posts.length})
                  </Typography>
                  
                  {/* Media Posts Section */}
                  {posts.filter(post => post.type === 'image' || post.type === 'video').length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ 
                        color: goldColor, 
                        mb: 2,
                        fontSize: '1.2rem',
                        fontWeight: 700,
                      }}>
                        Media Posts
                      </Typography>
                      <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
                        {posts
                          .filter(post => (post.type === 'image' || post.type === 'video') &&
                            (searchTerm === '' || 
                             (post.title && post.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (post.content && post.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (post.author?.username && post.author.username.toLowerCase().includes(searchTerm.toLowerCase())))
                          )
                          .map(post => (
                            <Grid  key={post.id}>
                              <PostCard post={post} />
                            </Grid>
                          ))}
                      </Grid>
                    </>
                  )}
                  
                  {/* Text Posts Section */}
                  {posts.filter(post => post.type === 'text').length > 0 && (
                    <>
                      {posts.filter(post => post.type === 'image' || post.type === 'video').length > 0 && (
                        <Divider sx={{ my: 4, borderColor: alpha(goldColor, 0.2) }} />
                      )}
                      
                      <Typography variant="h6" sx={{ 
                        color: goldColor, 
                        mb: 2,
                        fontSize: '1.2rem',
                        fontWeight: 700,
                      }}>
                        Text Posts
                      </Typography>
                      <Grid container spacing={3} justifyContent="center">
                        {posts
                          .filter(post => post.type === 'text' &&
                            (searchTerm === '' || 
                             (post.title && post.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (post.content && post.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (post.author?.username && post.author.username.toLowerCase().includes(searchTerm.toLowerCase())))
                          )
                          .map(post => (
                            <Grid  key={post.id}>
                              <PostCard post={post} />
                            </Grid>
                          ))}
                      </Grid>
                    </>
                  )}
                  
                  {posts.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <PostAdd sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
                      <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>No Posts Found</Typography>
                      <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>No posts have been created yet.</Typography>
                    </Box>
                  )}
                </>
              )}

              {activeSection === 'communities' && (
                <>
                  <Typography variant="h5" sx={{ 
                    ...discordFont, 
                    color: isDark ? '#fff' : deepGreen, 
                    mb: 3,
                    fontSize: '1.5rem',
                  }}>
                    All Communities ({communities.length})
                  </Typography>
                  {communities.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Group sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
                      <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>No Communities Found</Typography>
                      <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>No communities have been created yet.</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3} justifyContent="center">
                      {communities
                        .filter(community => 
                          searchTerm === '' || 
                          (community.name && community.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (community.description && community.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (community.owner?.username && community.owner.username.toLowerCase().includes(searchTerm.toLowerCase()))
                        )
                        .map(community => (
                          <Grid  key={community.id}>
                            <CommunityCard community={community} />
                          </Grid>
                        ))}
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminStats;